import React from 'react'
import { Modal, Table } from 'antd'
import styled from 'styled-components'
import { STOCK_ROLE_PERMISSION_ROWS } from 'src/utils/permissions'
import { CUSTOM_ROLES_LEARN_HINT } from 'src/utils/venueRoleCapabilities'

const StyledContainer = styled.div`
  padding: 8px 16px 16px 16px;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
`

const Paragraph = styled.p`
  font-size: 14px;
  color: #555;
  margin-bottom: 12px;
  line-height: 1.4;
`

const TipBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae7ff;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 12px;
  font-size: 12px;
  color: #1890ff;
  line-height: 1.4;
`

const RolePermissionsModal = ({ visible, onCancel }) => {
  const columns = [
    {
      title: 'Feature',
      dataIndex: 'feature',
      key: 'feature',
      width: 240,
      render: text => <strong>{text}</strong>,
    },
    {
      title: 'Admin',
      dataIndex: 'admin',
      key: 'admin',
      align: 'center',
      width: 88,
      render: value => (
        <span style={{ fontSize: '18px' }}>{value ? '✅' : '❌'}</span>
      ),
    },
    {
      title: 'Head Chef / GM',
      dataIndex: 'headChefGM',
      key: 'headChefGM',
      align: 'center',
      width: 120,
      render: value => (
        <span style={{ fontSize: '16px' }}>
          {value === 'view' ? '👁️ View' : value ? '✅' : '❌'}
        </span>
      ),
    },
    {
      title: 'Chef',
      dataIndex: 'chef',
      key: 'chef',
      align: 'center',
      width: 88,
      render: value => (
        <span style={{ fontSize: '16px' }}>
          {value === 'limited' ? '⚠️ Limited' : value ? '✅' : '❌'}
        </span>
      ),
    },
    {
      title: 'Order Only',
      dataIndex: 'orderOnly',
      key: 'orderOnly',
      align: 'center',
      width: 100,
      render: value => (
        <span style={{ fontSize: '16px' }}>{value ? '✅' : '❌'}</span>
      ),
    },
    {
      title: 'Kitchen Porter',
      dataIndex: 'kitchenPorter',
      key: 'kitchenPorter',
      align: 'center',
      width: 120,
      render: value => (
        <span style={{ fontSize: '16px' }}>{value ? '✅' : '❌'}</span>
      ),
    },
  ]

  const withKitchenPorter = (row, kitchenPorter = false) => ({
    ...row,
    kitchenPorter,
  })

  const dataSource = [
    {
      key: '1',
      feature: 'User Management',
      admin: true,
      headChefGM: 'view',
      chef: false,
      orderOnly: false,
    },
    {
      key: '2',
      feature: 'Ordering (Shopping Lists, Basket, Checkout)',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: true,
    },
    {
      key: '3',
      feature: 'Past Orders',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: true,
    },
    {
      key: '4',
      feature: 'Suppliers',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: false,
    },
    ...STOCK_ROLE_PERMISSION_ROWS,
    {
      key: '6',
      feature: 'Menu Builder / Recipes',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: false,
    },
    {
      key: '7',
      feature: 'Reports',
      admin: true,
      headChefGM: true,
      chef: 'limited',
      orderOnly: false,
    },
    {
      key: '8',
      feature: 'Gross Profit',
      admin: true,
      headChefGM: true,
      chef: false,
      orderOnly: false,
    },
    {
      key: '9',
      feature: 'Invoices',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: true,
    },
    {
      key: '10',
      feature: 'Subscriptions',
      admin: true,
      headChefGM: false,
      chef: false,
      orderOnly: false,
    },
    {
      key: '11',
      feature: 'Integrations',
      admin: true,
      headChefGM: false,
      chef: false,
      orderOnly: false,
    },
    {
      key: '12',
      feature: 'Settings',
      admin: true,
      headChefGM: false,
      chef: false,
      orderOnly: false,
    },
    {
      key: '13',
      feature: 'Activity Summary Emails',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: true,
    },
    {
      key: '14',
      feature: 'Credit Notes',
      admin: true,
      headChefGM: true,
      chef: false,
      orderOnly: false,
    },
    {
      key: '15',
      feature: 'Stats / Dashboard',
      admin: true,
      headChefGM: true,
      chef: false,
      orderOnly: false,
    },
    {
      key: 'food-safety-complete',
      feature: 'Food Safety — complete checks and view EHO reports',
      admin: true,
      headChefGM: true,
      chef: true,
      orderOnly: true,
      kitchenPorter: true,
    },
    {
      key: 'food-safety-manage',
      feature: 'Food Safety — manage setup',
      admin: true,
      headChefGM: true,
      chef: false,
      orderOnly: false,
      kitchenPorter: false,
    },
  ].map(row => withKitchenPorter(row, row.kitchenPorter))

  return (
    <Modal
      title='User Role Permissions'
      centered
      visible={visible}
      width={860}
      footer={null}
      onCancel={onCancel}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <StyledContainer>
        <Title>User Role Permissions</Title>
        <Paragraph>
          See what each role can access. Use roles to control who can see
          sensitive data like GP reports. Everyone can upload invoices.
        </Paragraph>
        <Paragraph>{CUSTOM_ROLES_LEARN_HINT}</Paragraph>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 760, y: 400 }}
          size='small'
        />
        <TipBox>
          💡 <strong>Tip:</strong> Use roles to restrict access to sensitive
          data like GP reports and stock variance. Everyone can upload invoices.
          Chefs can count stock and lock takes, but only Venue Admins can unlock
          or see expected quantities and variance.
        </TipBox>
      </StyledContainer>
    </Modal>
  )
}

export default RolePermissionsModal
